/**
 * Public API functions - accessible without admin authentication.
 * Includes menu, restaurant info, and order creation.
 */

import type {
  MenuPublic,
  OrderPublic,
  CreateOrderInput,
  OpeningHours,
} from '@chatealo/shared';
import {
  getCacheItem,
  setCacheItem,
  getMenuCacheKey,
  CACHE_TTL,
} from '../lib/cache';
import { apiFetch } from './api-base';

/**
 * Get menu for a restaurant
 * Uses CloudStorage cache to reduce API calls
 */
export async function getMenu(restaurantId: string): Promise<MenuPublic> {
  const cacheKey = getMenuCacheKey(restaurantId);

  // Try to get from cache first
  const cached = await getCacheItem<MenuPublic>(cacheKey);
  if (cached) {
    return cached;
  }

  // Fetch from API
  const menu = await apiFetch<MenuPublic>(`/api/restaurants/${restaurantId}/menu`);

  // Cache the result (fire and forget)
  setCacheItem(cacheKey, menu, CACHE_TTL.MENU).catch(() => {});

  return menu;
}

/**
 * Restaurant public info response
 */
export interface RestaurantPublicInfo {
  id: string;
  slug: string;
  name: string;
  telegramBotUsername: string;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  isOpen: boolean;
  openingHours: OpeningHours | null;
  closedDates: string[] | null;
  timezone: string;
  fees: {
    taxEnabled: boolean;
    taxRate: number;
    taxName: string;
  } | null;
  deliveryConfig: {
    fee: number;
    minOrder: number;
    estimatedTime: string;
    maxRadiusKm?: number;
  } | null;
  chargesEnabled: boolean;
  latitude: number | null;
  longitude: number | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
}

/**
 * Get restaurant info
 * NO CACHE - restaurant info includes critical real-time data (openingHours, isOpen, closedDates)
 * that must reflect changes immediately when the owner updates them
 */
export async function getRestaurant(restaurantId: string): Promise<RestaurantPublicInfo> {
  return apiFetch<RestaurantPublicInfo>(`/api/restaurants/${restaurantId}`);
}

/**
 * Create a new order
 */
export async function createOrder(input: CreateOrderInput): Promise<OrderPublic> {
  return apiFetch<OrderPublic>('/api/orders', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Get order details (for reorder)
 */
export async function getOrder(orderId: string): Promise<OrderPublic> {
  return apiFetch<OrderPublic>(`/api/orders/${orderId}`);
}

/**
 * Get order history for current customer at a restaurant
 */
export async function getOrderHistory(restaurantId: string): Promise<OrderPublic[]> {
  const response = await apiFetch<{ data: OrderPublic[] }>(`/api/orders/history?restaurantId=${encodeURIComponent(restaurantId)}`);
  return response.data;
}

/**
 * Cancel order (customer can only cancel PENDING orders)
 */
export async function cancelOrder(orderId: string): Promise<void> {
  await apiFetch(`/api/orders/${orderId}/cancel`, {
    method: 'POST',
  });
}

/**
 * Chain location for the location picker
 */
export interface ChainLocation {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  isOpen: boolean;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
}

/**
 * Chain locations response
 */
export interface ChainLocationsResponse {
  chain: {
    id: string;
    name: string;
    botUsername: string;
  };
  locations: ChainLocation[];
}

/**
 * Get locations for a chain (for location picker)
 */
export async function getChainLocations(chainId: string): Promise<ChainLocationsResponse> {
  const response = await apiFetch<{ data: ChainLocationsResponse }>(`/api/chains/${chainId}/locations`);
  return response.data;
}

/**
 * Bot resolution response
 * Used when the miniapp opens via direct link (t.me/bot?startapp) without restaurantId
 */
export interface BotResolution {
  type: 'restaurant' | 'chain';
  id: string;
}

const RESOLVE_CACHE_KEY = '__chatealo_resolved_bot';

/**
 * Resolve which restaurant/chain the current bot belongs to.
 * Caches the result in sessionStorage so subsequent loads (refresh/reopen)
 * skip the API call entirely.
 */
export async function resolveBot(): Promise<BotResolution> {
  // Check sessionStorage cache first (persists within the session)
  try {
    const cached = sessionStorage.getItem(RESOLVE_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached) as BotResolution;
    }
  } catch {
    // sessionStorage not available — continue with API call
  }

  const response = await apiFetch<{ data: BotResolution }>('/api/resolve/bot');
  const result = response.data;

  // Cache in sessionStorage for subsequent loads
  try {
    sessionStorage.setItem(RESOLVE_CACHE_KEY, JSON.stringify(result));
  } catch {
    // sessionStorage not available — no caching
  }

  return result;
}

